import React from "react";
import { StatusPill, type StatusVariant } from "../../atoms";
import {
  CollapsibleSection,
  DataTable,
  Pagination,
  ScrollableTableViewport,
  type DataTableColumn,
} from "../../molecules";
import type {
  ConnectionLogEntry,
  ConnectionLogStatus,
} from "../../../pages/Connectors/api";
import type { ConnectionLogsSectionProps } from "./ConnectionLogsSection.types";

/** Logs grow with row count; only scroll after this cap (hidden scrollbar). */
const LOGS_TABLE_MAX_SCROLL = "min(360px, 50vh)";

const STATUS_LABEL: Record<ConnectionLogStatus, string> = {
  success: "Success",
  na: "N/A",
  error: "Error",
};

const STATUS_VARIANT: Record<ConnectionLogStatus, StatusVariant> = {
  success: "success",
  na: "neutral",
  error: "error",
};
const PAGE_SIZE_OPTIONS = [5, 10, 25] as const;

const COLUMNS: Array<DataTableColumn<ConnectionLogEntry>> = [
  {
    key: "timestamp",
    header: "TIMESTAMP",
    width: "200px",
    bodyVariant: 3,
    emphasis: "medium",
    render: (row) => row.timestamp,
  },
  {
    key: "event",
    header: "EVENT",
    bodyVariant: 3,
    emphasis: "high",
    render: (row) => row.event,
  },
  {
    key: "status",
    header: "STATUS",
    width: "140px",
    align: "right",
    bodyVariant: 3,
    emphasis: "high",
    render: (row) => (
      <StatusPill variant={STATUS_VARIANT[row.status]}>
        {STATUS_LABEL[row.status]}
      </StatusPill>
    ),
  },
];

/**
 * Connection logs section on the Connection Detail page (UI-4).
 *
 * `DataTable` + `Pagination` use `ScrollableTableViewport` (sticky header,
 * scrollable body, pinned footer) like dataset preview / wizard tables.
 */
export const ConnectionLogsSection: React.FC<ConnectionLogsSectionProps> = ({
  logs,
  page,
  pageCount,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  expanded,
  onToggle,
  className,
}) => (
  <CollapsibleSection
    className={className}
    title="Connection logs"
    expanded={expanded}
    onToggle={onToggle}
  >
    <ScrollableTableViewport
      shrinkToContent
      maxScrollHeight={LOGS_TABLE_MAX_SCROLL}
      hideScrollbar
      variant="card"
      footer={
        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={onPageChange}
          pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
          onPageSizeChange={onPageSizeChange}
        />
      }
    >
      <DataTable
        chromeless
        stickyHeader
        columns={COLUMNS}
        rows={logs}
        rowKey={(row) => row.id}
      />
    </ScrollableTableViewport>
  </CollapsibleSection>
);

export default ConnectionLogsSection;
